import React, { useState } from "react";
import SectionHeader, { SectionWrapper, Reveal } from "./SectionHeader";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Globe, Facebook, Instagram, Youtube, ArrowUpRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactFooter = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    category: "brand",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (error) setError("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    if (!name) {
      setError("Please enter your name.");
      toast.error("Please enter your name.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      toast.error("Please enter a valid email address.");
      return;
    }
    if (message.length < 2) {
      setError("Please add a short message.");
      toast.error("Please add a short message.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/partners`, { ...form, name, email, message });
      toast.success("Signal received. Our team will be in touch.");
      setSubmitted(true);
      setForm({ name: "", email: "", company: "", category: "brand", message: "" });
    } catch (err) {
      // Surface backend validation/server errors clearly
      const detail = err?.response?.data?.detail;
      let msg = "Transmission failed. Please try again.";
      if (Array.isArray(detail) && detail.length) {
        msg = detail[0]?.msg || msg;
      } else if (typeof detail === "string") {
        msg = detail;
      } else if (err?.message) {
        msg = `Network error: ${err.message}`;
      }
      setError(msg);
      toast.error(msg);
      console.error("Partner inquiry submit failed", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SectionWrapper id="contact" className="pb-0">
      <SectionHeader
        code="// CH.10 — INITIATE CONTACT"
        title="Initiate.Signal"
        kicker="Partner with Bharat's most powerful independent youth media network."
        tid="contact"
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Form */}
        <Reveal className="lg:col-span-7">
          <div className="glass-card hud-corners p-8 md:p-10" data-testid="contact-form-wrap">
            <span className="hud-tr" />
            <span className="hud-bl" />
            <div className="flex items-center justify-between mb-8">
              <div className="micro-label text-cyan-400">TRANSMISSION · OPEN</div>
              <div className="micro-label text-white/40">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot mr-2 align-middle" />
                CHANNEL · SECURE
              </div>
            </div>

            {submitted ? (
              <div className="py-10 text-center" data-testid="contact-success">
                <div className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight glow-cyan text-cyan-300">
                  Signal Received.
                </div>
                <p className="text-white/60 mt-4 max-w-md mx-auto">
                  Your transmission has entered the queue. We&apos;ll reach out within 48h.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="btn-uth mt-8"
                  data-testid="contact-send-another"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="contact-form">
                <div className="flex flex-col gap-2">
                  <label className="micro-label text-white/50">Name</label>
                  <input
                    className="uth-input"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    data-testid="contact-input-name"
                    required
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="micro-label text-white/50">Email</label>
                  <input
                    className="uth-input"
                    type="email"
                    placeholder="you@brand.com"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    data-testid="contact-input-email"
                    required
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="micro-label text-white/50">Company</label>
                  <input
                    className="uth-input"
                    placeholder="Brand / agency / org"
                    value={form.company}
                    onChange={(e) => update("company", e.target.value)}
                    data-testid="contact-input-company"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="micro-label text-white/50">Category</label>
                  <select
                    className="uth-input"
                    value={form.category}
                    onChange={(e) => update("category", e.target.value)}
                    data-testid="contact-input-category"
                  >
                    <option value="brand">Brand</option>
                    <option value="agency">Agency</option>
                    <option value="government">Government / Public</option>
                    <option value="creator">Creator</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="flex flex-col gap-2 md:col-span-2">
                  <label className="micro-label text-white/50">Message</label>
                  <textarea
                    className="uth-input min-h-[120px] resize-none"
                    placeholder="Tell us about the mission..."
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    data-testid="contact-input-message"
                    required
                  />
                </div>
                <div className="md:col-span-2 flex items-center justify-between gap-4 mt-2">
                  <div className="micro-label text-white/35">
                    We reply within <span className="text-cyan-400">48h</span>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-uth disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-submit"
                  >
                    {submitting ? "Transmitting..." : "Transmit Signal"}
                    {!submitting && <ArrowUpRight size={14} />}
                  </button>
                </div>
                {error && (
                  <div
                    className="md:col-span-2 mt-2 px-4 py-3 border border-fuchsia-500/40 bg-fuchsia-500/[0.06] text-fuchsia-200 text-sm font-mono-uth tracking-wider"
                    data-testid="contact-error"
                    role="alert"
                  >
                    <span className="text-fuchsia-400 mr-2">⚠</span>
                    {error}
                  </div>
                )}
              </form>
            )}
          </div>
        </Reveal>

        {/* Contact panel */}
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="flex flex-col gap-6">
            <div className="glass-card hud-corners p-8" data-testid="contact-info">
              <span className="hud-tr" />
              <span className="hud-bl" />
              <div className="micro-label text-fuchsia-400 mb-4">DIRECT LINE</div>
              <a
                href="mailto:hello@uthtimes.com"
                className="flex items-center gap-3 group mb-4"
                data-testid="contact-email"
              >
                <Mail size={18} className="text-cyan-400" />
                <span className="font-display font-bold text-lg md:text-xl group-hover:text-cyan-300 transition-colors">
                  hello@uthtimes.com
                </span>
              </a>
              <a
                href="https://www.uthtimes.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 group"
                data-testid="contact-website"
              >
                <Globe size={18} className="text-fuchsia-400" />
                <span className="font-display font-bold text-lg md:text-xl group-hover:text-fuchsia-300 transition-colors">
                  www.uthtimes.com
                </span>
              </a>
            </div>

            <div className="glass-card hud-corners p-8" data-testid="contact-socials">
              <span className="hud-tr" />
              <span className="hud-bl" />
              <div className="micro-label text-yellow-300 mb-5">CHANNELS · LIVE</div>
              <div className="flex flex-col gap-3">
                {[
                  { Icon: Facebook, label: "@uthtime.rc", href: "https://facebook.com/uthtime.rc", tid: "social-fb" },
                  { Icon: Instagram, label: "@uthtimes01", href: "https://instagram.com/uthtimes01", tid: "social-ig" },
                  { Icon: Youtube, label: "@uthtimes", href: "https://youtube.com/@uthtimes", tid: "social-yt" },
                ].map(({ Icon, label, href, tid }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={tid}
                    className="flex items-center justify-between py-3 border-b border-white/5 group"
                  >
                    <span className="flex items-center gap-3">
                      <Icon size={16} className="text-white/70 group-hover:text-cyan-300 transition-colors" />
                      <span className="font-body text-white/80 group-hover:text-white transition-colors">
                        {label}
                      </span>
                    </span>
                    <ArrowUpRight size={14} className="text-white/40 group-hover:text-cyan-300 transition-colors" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Footer */}
      <footer className="mt-20 md:mt-28 border-t border-cyan-500/15 pt-8 pb-10" data-testid="site-footer">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400 pulse-dot" />
            <span className="font-display font-black text-sm tracking-tight">
              U&apos;TH.TIMES
            </span>
            <span className="micro-label text-white/35">// UTH_MEDIA_9X</span>
          </div>
          <div className="micro-label text-white/40 text-center">
            U&apos;th Times Integrated Media Labs Pvt Ltd · All Rights Reserved · 2026
          </div>
          <div className="micro-label text-white/35">
            SIGNAL · STABLE
          </div>
        </div>
      </footer>
    </SectionWrapper>
  );
};

export default ContactFooter;
